import React, { useState, useEffect, useCallback } from 'react';
import { Shovel, Package, TrendingUp, Plus, Settings, X, Check, Edit2, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { excavationDatabase } from '../data/excavation';
import ExcavationMaterialCard from './ExcavationMaterialCard';
import ExcavationSummaryCard from './ExcavationSummaryCard';
import { toast } from 'sonner';

const ExcavationCalculator = ({ excavationMaterials, setExcavationMaterials }) => {
  const [selectedMaterial, setSelectedMaterial] = useState('');
  const [bankCubicYards, setBankCubicYards] = useState('');
  const [costPerTon, setCostPerTon] = useState('');
  const [customWeight, setCustomWeight] = useState('');
  const [customSwellFactor, setCustomSwellFactor] = useState('');

  // Helper to generate unique IDs
  const generateId = useCallback(() => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Custom materials management
  const [customMaterials, setCustomMaterials] = useState(() => {
    const saved = localStorage.getItem('customExcavationMaterials');
    return saved ? JSON.parse(saved) : [];
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMaterial, setNewMaterial] = useState({
    name: '',
    category: 'Custom',
    tonsPerBCY: '',
    swellFactor: '1.25'
  });

  // Save custom materials to localStorage
  useEffect(() => {
    localStorage.setItem('customExcavationMaterials', JSON.stringify(customMaterials));
  }, [customMaterials]);

  // Combine default and custom materials
  const allMaterials = [...excavationDatabase, ...customMaterials];

  const addCustomMaterial = () => {
    if (!newMaterial.name.trim()) {
      toast.error('Please enter a material name');
      return;
    }
    if (!newMaterial.tonsPerBCY || parseFloat(newMaterial.tonsPerBCY) <= 0) {
      toast.error('Please enter a valid weight (tons/BCY)');
      return;
    }
    const material = {
      id: `custom_${Date.now()}`,
      name: newMaterial.name,
      category: newMaterial.category || 'Custom',
      tonsPerBCY: parseFloat(newMaterial.tonsPerBCY),
      swellFactor: parseFloat(newMaterial.swellFactor) || 1.25,
      description: 'Custom material',
      isCustom: true
    };
    setCustomMaterials([...customMaterials, material]);
    setNewMaterial({ name: '', category: 'Custom', tonsPerBCY: '', swellFactor: '1.25' });
    setShowAddForm(false);
    toast.success(`Material "${material.name}" added`);
  };

  const removeCustomMaterial = (id) => {
    setCustomMaterials(customMaterials.filter(m => m.id !== id));
    toast.success('Material removed');
  };

  const addMaterial = () => {
    if (!selectedMaterial || !bankCubicYards) {
      toast.error('Please select a material and enter bank cubic yards');
      return;
    }

    const bcy = parseFloat(bankCubicYards);
    if (bcy <= 0) {
      toast.error('Bank cubic yards must be greater than 0');
      return;
    }

    const materialInfo = allMaterials.find(m => m.name === selectedMaterial);
    if (!materialInfo) {
      toast.error('Invalid material selected');
      return;
    }

    // Use custom values if provided
    const effectiveWeight = parseFloat(customWeight) || materialInfo.tonsPerBCY;
    const effectiveSwell = parseFloat(customSwellFactor) || materialInfo.swellFactor;

    const looseCY = bcy * effectiveSwell;
    const totalTons = bcy * effectiveWeight;
    const cost = parseFloat(costPerTon) || 0;

    const newMaterialItem = {
      id: generateId(),
      name: materialInfo.name,
      category: materialInfo.category,
      tonsPerBCY: effectiveWeight,
      originalTonsPerBCY: materialInfo.tonsPerBCY,
      swellFactor: effectiveSwell,
      originalSwellFactor: materialInfo.swellFactor,
      customWeight: customWeight ? parseFloat(customWeight) : null,
      customSwellFactor: customSwellFactor ? parseFloat(customSwellFactor) : null,
      bankCubicYards: bcy,
      looseCubicYards: looseCY,
      totalTons: totalTons,
      totalPounds: totalTons * 2000,
      costPerTon: cost,
      totalCost: totalTons * cost,
      addedAt: new Date().toISOString()
    };

    setExcavationMaterials([...excavationMaterials, newMaterialItem]);
    toast.success(`${materialInfo.name} added successfully!`);
    
    setSelectedMaterial('');
    setBankCubicYards('');
    setCostPerTon('');
    setCustomWeight('');
    setCustomSwellFactor('');
  };

  const updateMaterial = (id, updatedData) => {
    setExcavationMaterials(excavationMaterials.map(material => {
      if (material.id === id) {
        const looseCY = updatedData.bankCubicYards * material.swellFactor;
        const totalTons = updatedData.bankCubicYards * material.tonsPerBCY;
        const totalPounds = totalTons * 2000;
        return { ...material, ...updatedData, looseCubicYards: looseCY, totalTons, totalPounds };
      }
      return material;
    }));
    toast.success('Material updated');
  };

  const deleteMaterial = (id) => {
    const materialToDelete = excavationMaterials.find(m => m.id === id);
    setExcavationMaterials(excavationMaterials.filter(material => material.id !== id));
    toast.error(`${materialToDelete?.name || 'Material'} removed`);
  };

  const clearAll = () => {
    setExcavationMaterials([]);
    localStorage.removeItem('excavationMaterials');
    toast.info('All excavation materials cleared');
  };

  // Calculate totals
  const materialTotals = excavationMaterials.reduce((acc, material) => {
    if (!acc[material.name]) {
      acc[material.name] = {
        name: material.name,
        category: material.category,
        totalBankCY: 0,
        totalLooseCY: 0,
        totalTons: 0,
        count: 0
      };
    }
    acc[material.name].totalBankCY += material.bankCubicYards;
    acc[material.name].totalLooseCY += material.looseCubicYards;
    acc[material.name].totalTons += material.totalTons;
    acc[material.name].count += 1;
    return acc;
  }, {});

  const grandTotal = {
    totalBankCY: excavationMaterials.reduce((sum, m) => sum + m.bankCubicYards, 0),
    totalLooseCY: excavationMaterials.reduce((sum, m) => sum + m.looseCubicYards, 0),
    totalTons: excavationMaterials.reduce((sum, m) => sum + m.totalTons, 0),
    itemCount: excavationMaterials.length
  };

  const groupedMaterials = allMaterials.reduce((acc, material) => {
    const category = material.category || 'Custom';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(material);
    return acc;
  }, {});

  const selectedMaterialData = allMaterials.find(m => m.name === selectedMaterial);

  return (
    <div className="space-y-8">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-card border border-border rounded-lg p-6 shadow-card transition-smooth hover:shadow-elegant">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-muted-foreground">Total Items</span>
            <Package className="w-5 h-5 text-primary" />
          </div>
          <p className="text-3xl font-bold text-foreground">{grandTotal.itemCount}</p>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-6 shadow-card transition-smooth hover:shadow-elegant">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-muted-foreground">Total Volume</span>
            <TrendingUp className="w-5 h-5 text-accent" />
          </div>
          <div>
            <p className="text-2xl font-bold text-foreground">
              {grandTotal.totalBankCY.toFixed(2)}
              <span className="text-base text-muted-foreground ml-2">BCY</span>
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {grandTotal.totalLooseCY.toFixed(2)} LCY (loose)
            </p>
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-6 shadow-card transition-smooth hover:shadow-elegant">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-muted-foreground">Total Weight</span>
            <Shovel className="w-5 h-5 text-success" />
          </div>
          <div>
            <p className="text-3xl font-bold text-foreground">
              {grandTotal.totalTons.toFixed(2)}
              <span className="text-lg text-muted-foreground ml-2">tons</span>
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {(grandTotal.totalTons * 2000).toLocaleString(undefined, { maximumFractionDigits: 0 })} lbs
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Add Material Form */}
        <div className="lg:col-span-1">
          <Card className="border border-border shadow-card bg-card sticky top-4">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Plus className="w-5 h-5 text-primary" />
                  Add Excavation
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-4 h-4 text-muted-foreground hover:text-primary cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-xs">
                        <p className="text-sm">Calculate excavation costs by selecting material type and entering bank cubic yards. The calculator applies swell factors automatically. You can add custom materials with specific weight and swell factors.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Settings className="w-4 h-4 mr-1" />
                      Manage
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Manage Custom Excavation Materials</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      {showAddForm ? (
                        <Card className="bg-muted/30">
                          <CardContent className="pt-4">
                            <div className="grid grid-cols-2 gap-3">
                              <div className="space-y-1">
                                <Label className="text-xs">Material Name</Label>
                                <Input
                                  value={newMaterial.name}
                                  onChange={(e) => setNewMaterial({...newMaterial, name: e.target.value})}
                                  placeholder="e.g., Heavy Clay"
                                  className="h-8 text-sm"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Category</Label>
                                <Input
                                  value={newMaterial.category}
                                  onChange={(e) => setNewMaterial({...newMaterial, category: e.target.value})}
                                  placeholder="Custom"
                                  className="h-8 text-sm"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Tons per BCY</Label>
                                <Input
                                  type="number"
                                  value={newMaterial.tonsPerBCY}
                                  onChange={(e) => setNewMaterial({...newMaterial, tonsPerBCY: e.target.value})}
                                  placeholder="1.5"
                                  className="h-8 text-sm"
                                  min="0"
                                  step="0.01"
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs">Swell Factor</Label>
                                <Input
                                  type="number"
                                  value={newMaterial.swellFactor}
                                  onChange={(e) => setNewMaterial({...newMaterial, swellFactor: e.target.value})}
                                  placeholder="1.25"
                                  className="h-8 text-sm"
                                  min="1"
                                  step="0.01"
                                />
                              </div>
                            </div>
                            <div className="flex gap-2 mt-3">
                              <Button size="sm" onClick={addCustomMaterial}>
                                <Check className="w-3 h-3 mr-1" />
                                Add Material
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setShowAddForm(false)}>
                                Cancel
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ) : (
                        <Button variant="outline" size="sm" onClick={() => setShowAddForm(true)} className="w-full">
                          <Plus className="w-4 h-4 mr-2" />
                          Add New Custom Material
                        </Button>
                      )}

                      {customMaterials.length > 0 && (
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Your Custom Materials</Label>
                          {customMaterials.map(material => (
                            <div key={material.id} className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg">
                              <span className="flex-1 text-sm">{material.name}</span>
                              <span className="text-xs text-muted-foreground">{material.tonsPerBCY} t/BCY</span>
                              <span className="text-xs text-muted-foreground">{material.swellFactor}x swell</span>
                              <Button size="sm" variant="ghost" onClick={() => removeCustomMaterial(material.id)} className="h-7 px-2">
                                <X className="w-3 h-3 text-destructive" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="excavMaterial" className="text-sm font-medium text-foreground">
                  Material Type
                </Label>
                <Select value={selectedMaterial} onValueChange={(val) => {
                  setSelectedMaterial(val);
                  setCustomWeight('');
                  setCustomSwellFactor('');
                }}>
                  <SelectTrigger id="excavMaterial" className="w-full">
                    <SelectValue placeholder="Select material" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[400px]">
                    {Object.entries(groupedMaterials).map(([category, mats]) => (
                      <div key={category}>
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50">
                          {category}
                        </div>
                        {mats.map(material => (
                          <SelectItem key={material.name} value={material.name}>
                            <div className="flex items-center justify-between w-full pr-4">
                              <span className="text-sm">{material.name}</span>
                              <span className="text-xs text-muted-foreground ml-2">
                                {material.tonsPerBCY} t/BCY
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {selectedMaterialData && (
                <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Category:</span>
                    <span className="font-semibold text-foreground">{selectedMaterialData.category}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Default weight:</span>
                    <span className="font-semibold text-primary">{selectedMaterialData.tonsPerBCY} tons/BCY</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Default swell:</span>
                    <span className="font-semibold text-accent">{selectedMaterialData.swellFactor}x</span>
                  </div>
                  {selectedMaterialData.description && (
                    <p className="text-xs text-muted-foreground mt-2 pt-2 border-t border-border">
                      {selectedMaterialData.description}
                    </p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="bankCY" className="text-sm font-medium text-foreground">
                    Bank Cubic Yards
                  </Label>
                  <Input
                    id="bankCY"
                    type="number"
                    placeholder="Enter BCY"
                    value={bankCubicYards}
                    onChange={(e) => setBankCubicYards(e.target.value)}
                    min="0"
                    step="0.01"
                    className="w-full"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="costPerTon" className="text-sm font-medium text-foreground">
                    $/Ton
                  </Label>
                  <Input
                    id="costPerTon"
                    type="number"
                    placeholder="$0.00"
                    value={costPerTon}
                    onChange={(e) => setCostPerTon(e.target.value)}
                    min="0"
                    step="0.01"
                    className="w-full"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="customWeight" className="text-sm font-medium text-foreground">
                    Custom tons/BCY
                  </Label>
                  <Input
                    id="customWeight"
                    type="number"
                    placeholder={selectedMaterialData?.tonsPerBCY?.toString() || "Override"}
                    value={customWeight}
                    onChange={(e) => setCustomWeight(e.target.value)}
                    min="0"
                    step="0.01"
                    className="w-full"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="customSwell" className="text-sm font-medium text-foreground">
                    Custom Swell
                  </Label>
                  <Input
                    id="customSwell"
                    type="number"
                    placeholder={selectedMaterialData?.swellFactor?.toString() || "1.25"}
                    value={customSwellFactor}
                    onChange={(e) => setCustomSwellFactor(e.target.value)}
                    min="1"
                    step="0.01"
                    className="w-full"
                  />
                </div>
              </div>

              {selectedMaterialData && bankCubicYards > 0 && (
                <div className="bg-success/10 border border-success/30 rounded-lg p-3 space-y-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Loose Volume:</p>
                      <p className="text-xl font-bold text-accent">
                        {(parseFloat(bankCubicYards) * (parseFloat(customSwellFactor) || selectedMaterialData.swellFactor)).toFixed(2)} LCY
                      </p>
                      {customSwellFactor && <p className="text-xs text-primary">(custom swell)</p>}
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Estimated Weight:</p>
                      <p className="text-xl font-bold text-success">
                        {(parseFloat(bankCubicYards) * (parseFloat(customWeight) || selectedMaterialData.tonsPerBCY)).toFixed(2)} tons
                      </p>
                      {customWeight && <p className="text-xs text-primary">(custom weight)</p>}
                    </div>
                  </div>
                  {costPerTon > 0 && (
                    <div className="mt-2 pt-2 border-t border-success/30">
                      <p className="text-sm text-muted-foreground mb-1">Estimated Cost:</p>
                      <p className="text-xl font-bold text-success">
                        ${(parseFloat(bankCubicYards) * (parseFloat(customWeight) || selectedMaterialData.tonsPerBCY) * parseFloat(costPerTon)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                  )}
                </div>
              )}

              <Button
                onClick={addMaterial}
                className="w-full bg-primary hover:bg-primary/90 mt-4"
                size="lg"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Material
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Materials List */}
        <div className="lg:col-span-1 space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-semibold text-foreground">Excavation List</h2>
            {excavationMaterials.length > 0 && (
              <Button
                variant="outline"
                onClick={clearAll}
                size="sm"
                className="border-destructive/50 text-destructive hover:bg-destructive/10"
              >
                Clear All
              </Button>
            )}
          </div>
          
          {excavationMaterials.length === 0 ? (
            <div className="bg-card border border-dashed border-border rounded-lg p-12 text-center">
              <Shovel className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
              <h3 className="text-xl font-semibold text-foreground mb-2">No Excavations Added</h3>
              <p className="text-muted-foreground">
                Select material and enter bank cubic yards to calculate
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {excavationMaterials.map(material => (
                <ExcavationMaterialCard
                  key={material.id}
                  material={material}
                  onUpdate={updateMaterial}
                  onDelete={deleteMaterial}
                />
              ))}
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="lg:col-span-1">
          <ExcavationSummaryCard
            materialTotals={Object.values(materialTotals)}
            grandTotal={grandTotal}
          />
        </div>
      </div>
    </div>
  );
};

export default ExcavationCalculator;